import { User } from 'typings/auth'
import { LoginsTypes } from 'constants/index'

type getLoginSuccessDataType = {
    user: User
    loginType?: string
    externalStep: 'LOGIN' | 'PHASED_LOGIN' | 'PHASED_LOGIN_CADASTRO' | 'RECOVER_PASSWORD_LOGIN'
    cip?: string
    birthDate?: string
    rememberMe?: boolean
}

type getLoginFailureDataType = {
    externalStepFailure: 'LOGIN_FAILURE'
    loginType?: string
}

type getCurrentStepsType = {
    [index: string]: string
}
// eslint-disable-next-line prefer-destructuring
const ANALYTICS_ACCESS_OPTIONS: {
    [key: string]: string | undefined
} = LoginsTypes.DATA.LOGIN.ACCESS

function getLoginUserProfile(user: User): string {
    if (user.tokenType === 'FAP') {
        return LoginsTypes.DATA.LOGIN.USER_TYPE.FAP_ORPHAN
    }
    if (user.limitedFlow) {
        return LoginsTypes.DATA.LOGIN.USER_TYPE.CIP_ORPHAN
    }
    if (user.isInvalid) {
        return LoginsTypes.DATA.LOGIN.USER_TYPE.INVALID_CIP_MDM
    }
    if (user.underage) {
        return LoginsTypes.DATA.LOGIN.USER_TYPE.UNDERAGE
    }
    if (user.loginNav) {
        return LoginsTypes.DATA.LOGIN.USER_TYPE.COMPLETE
    }
    return LoginsTypes.DATA.LOGIN.USER_TYPE.INCOMPLETE
}

const getCurrentStep = (value: string) => {
    const steps: getCurrentStepsType = {
        CPF: 'Login CPF',
        Automático_pós_cadastro: 'Cadastro concluído',
        CIP: 'Login CIP',
    }

    return steps[value] || value
}

function getLoginAccessType(user: User, loginType?: string): string | undefined {
    if (!user.tokenType) {
        return loginType
    }

    const POSITION = `${user.tokenType}_${loginType}`
    return ANALYTICS_ACCESS_OPTIONS[POSITION] || loginType
}

function getUserIdOrCipWithBirthDate(user: User, cip?: string, birthDate?: string): string {
    if (user.limitedFlow) {
        return `${cip as string}&${(user.birthDate || birthDate)?.replace(/\//g, '')}`
    }
    return user.id ? user.id.toString() : ''
}

export function getLoginFailureData({ loginType, externalStepFailure }: getLoginFailureDataType) {
    const events = {
        LOGIN_FAILURE: {
            event: LoginsTypes.EVENTS.LOGIN_FAILURE,
            funnel: {
                name: LoginsTypes.FUNNELS.LOGIN_FASEADO,
                current_step: getCurrentStep(loginType!),
                previous_step: undefined,
            },
            page: {
                // eslint-disable-next-line no-undef
                path: document.location.pathname,
                // eslint-disable-next-line no-undef
                title: document.title,
            },
            error: {
                message: 'Unauthenticated Client',
            },
        },
    }

    return events[externalStepFailure]
}

export function getLoginSuccessData({ user, loginType, externalStep, cip, birthDate, rememberMe = false }: getLoginSuccessDataType) {
    const events = {
        LOGIN: {
            access: getLoginAccessType(user, loginType),
            event: LoginsTypes.EVENTS.LOGIN_SUCCESS,
            user: getLoginUserProfile(user),
            userId: getUserIdOrCipWithBirthDate(user, cip, birthDate),
        },
        PHASED_LOGIN: {
            event: LoginsTypes.EVENTS.LOGIN_SUCCESS,
            funnel: {
                name: LoginsTypes.FUNNELS.LOGIN_FASEADO,
                current_step: getCurrentStep(loginType!),
                previous_step: undefined,
            },
            page: {
                // eslint-disable-next-line no-undef
                path: document.location.pathname,
                // eslint-disable-next-line no-undef
                title: document.title,
            },
            user: {
                id: getUserIdOrCipWithBirthDate(user, cip, birthDate),
                ...(cip ? { cip } : {}),
                cpf: user.federalTaxId,
                access: getLoginAccessType(user, loginType),
                profile: getLoginUserProfile(user),
            },
            login_options: {
                remember_me: String(rememberMe),
            },
        },
        PHASED_LOGIN_CADASTRO: {
            event: LoginsTypes.EVENTS.LOGIN_SUCCESS,
            funnel: {
                name: LoginsTypes.FUNNELS.CADASTRO_WEB,
                current_step: 'Cadastro concluído',
                previous_step: undefined,
            },
            page: {
                // eslint-disable-next-line no-undef
                path: document.location.pathname,
                // eslint-disable-next-line no-undef
                title: document.title,
            },
            user: {
                id: getUserIdOrCipWithBirthDate(user, cip, birthDate),
                ...(cip ? { cip } : {}),
                cpf: user.federalTaxId,
                access: 'Automático pós cadastro',
                profile: getLoginUserProfile(user),
            },
        },
        RECOVER_PASSWORD_LOGIN: {
            event: LoginsTypes.EVENTS.LOGIN_SUCCESS,
            funnel: {
                name: LoginsTypes.FUNNELS.RECOVER_PASSWORD_LOGIN,
                current_step: 'Criação de nova senha',
                previous_step: 'Inserir token',
            },
            page: {
                // eslint-disable-next-line no-undef
                path: document.location.pathname,
                // eslint-disable-next-line no-undef
                title: document.title,
            },
            user: {
                id: getUserIdOrCipWithBirthDate(user, cip, birthDate),
                ...(cip ? { cip } : {}),
                cpf: user.federalTaxId,
                access: 'Reset Senha',
                profile: getLoginUserProfile(user),
            },
        },
    }

    return events[externalStep]
}
